import React, {
  useMemo,
  useState,
  useEffect,
  createContext,
  PropsWithChildren,
} from 'react';

import { Feature } from 'types';
import { AppRoutes } from 'config';
import { LinkProps } from 'components';
import { useAppSelector } from 'store';
import { canPlanTrips } from 'utils/trips-role';
import { useTrainingMeQuery } from 'store/api/training';
import { useMailBadgeQuery } from 'store/api/research-mail';
import { useMyTripsHasSentQuery } from 'store/api/trips/nav';
import { canUseBooking, canUseResearch } from 'utils/booking';
import { RouteObject, getRouteObjects } from 'config/route-objects';
import { selectAuthUserRole, selectAuthUserFeatures } from 'store/slices/auth';

interface SidebarContextProps {
  isOpened: boolean;
  links: LinkProps[];
  setOpened: (isOpened: boolean) => void;
}

const getInitialState = (): SidebarContextProps => ({
  links: [],
  isOpened: false,
  setOpened: () => {},
});

export const SidebarContext =
  createContext<SidebarContextProps>(getInitialState());

export const SidebarContextProvider = ({ children }: PropsWithChildren) => {
  const [isOpened, setOpened] = useState(getInitialState().isOpened);
  const authUserFeatures = useAppSelector(selectAuthUserFeatures);
  const userRole = useAppSelector(selectAuthUserRole);
  // REP PERFORMANCE: the role's rep_scoreboard key (Company Settings >
  // Permissions; owner 25 Oct: "need to add them to permissions").
  const showRepPerformance = Boolean(
    authUserFeatures?.includes(Feature.REP_SCOREBOARD)
  );
  // TRAINING: the training key (every trainer's reps), and anyone who trains
  // a rep, whatever the role (the server answers both in /training/me).
  const { data: training } = useTrainingMeQuery(undefined, {
    skip: !userRole,
  });
  const showTraining = Boolean(training?.canSeeAll || training?.isTrainer);
  // BOOKING and RESEARCH: the role's `booking` / `research` ticks (owner
  // 30 Oct 2026, Company Settings > Permissions; no company switch).
  const showBooking = canUseBooking(authUserFeatures);
  const showResearch = canUseResearch(authUserFeatures);
  // RESEARCH MAIL: Mail is a tab inside Research (owner 3 Nov), so its count
  // (unread + follow-ups due; Ops caches it 60 s) shows on Research.
  const { data: mailBadge } = useMailBadgeQuery(undefined, {
    skip: !showResearch,
    pollingInterval: 60_000,
  });
  const mailCount = (mailBadge?.unread ?? 0) + (mailBadge?.followUpDue ?? 0);
  // PAYROLL and TIME OFF are plain ticks (`payroll`, `time_off`; owner 2 Nov
  // 2026), like every other page.
  // TRIPS: one entry. A planner (the `trips` tick, a Manager leading an event)
  // opens the team view; anyone else with a sent trip opens their own trip.
  // Asked again whenever the app comes back to the screen, so a trip sent
  // while it sat in the background shows without signing in again (a send
  // also refreshes it, app-private).
  const { data: myTrips, refetch: refetchMyTrips } = useMyTripsHasSentQuery(
    undefined,
    { skip: !userRole }
  );
  const plansTrips = canPlanTrips(userRole, myTrips, authUserFeatures);
  const showTrips = plansTrips || Boolean(myTrips?.hasSent);
  useEffect(() => {
    if (!userRole) {
      return undefined;
    }
    const onShow = () => {
      if (document.visibilityState === 'visible') {
        void refetchMyTrips();
      }
    };
    document.addEventListener('visibilitychange', onShow);
    return () => document.removeEventListener('visibilitychange', onShow);
  }, [userRole, refetchMyTrips]);

  const links = useMemo(() => {
    const mapLinks = (items: RouteObject[]): LinkProps[] => {
      return items
        .filter(
          ({
            path,
            title,
            features,
            tripsOnly,
            bookingOnly,
            researchOnly,
            hideForRoles,
            trainingOnly,
            hideInSidebar,
            repPerformanceOnly,
          }) => {
            if (
              !path ||
              !title ||
              hideInSidebar ||
              (repPerformanceOnly && !showRepPerformance) ||
              (trainingOnly && !showTraining) ||
              (bookingOnly && !showBooking) ||
              (researchOnly && !showResearch) ||
              (tripsOnly && !showTrips) ||
              (userRole && hideForRoles?.includes(userRole))
            ) {
              return false;
            }

            return features
              ? features.some((feature) => authUserFeatures?.includes(feature))
              : true;
          }
        )
        .map(({ icon, path, title, tripsOnly, children: linkChildren }) => ({
          icon,
          title,
          items: linkChildren ? mapLinks(linkChildren) : undefined,
          // Trips: the team view for a planner, their own trip otherwise;
          // lit on either page.
          path: tripsOnly && !plansTrips ? AppRoutes.MyTrip.Home : path,
          count: path === AppRoutes.Research.Home ? mailCount : undefined,
          activeOn: tripsOnly
            ? [AppRoutes.Trips.Home, AppRoutes.MyTrip.Home]
            : undefined,
        })) as LinkProps[];
    };

    return mapLinks(getRouteObjects());
  }, [
    authUserFeatures,
    showBooking,
    showResearch,
    mailCount,
    showTraining,
    showRepPerformance,
    showTrips,
    plansTrips,
  ]);

  const value = {
    links,
    isOpened,
    setOpened,
  };

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
};
